import { Box, Button, Checkbox, HStack, Image, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Radio, RadioGroup, Spinner, Stack, Table, TableContainer, Tbody, Td, Text, Textarea, Th, Thead, Tr, useDisclosure, useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { adminDeleteProfiles, adminGetProfiles, adminSuspendProfiles } from "../../../api";
import AdminOnly from "../../../components/Auth/AdminOnly";
import MyIcon from "../../../components/Icon/MyIcon";
import ImageModal from "../../../components/Modals/ImageModal";
import { colors } from "../../../styles";
import { IAdminProfile, IAdminProfileList, IAdminSuspend } from "../../../types";
import { formatDateTimeKor } from "../../../utils";
import SuspendModal from "./SuspendModal";

export default function Profiles() {

    const [isSuspendedOnly, setIsSuspendedOnly] = useState<string>("all");

    const suspendedOnlyParam = useMemo(() => {
        if (isSuspendedOnly === "all") return undefined;
        return isSuspendedOnly === "true";
    }, [isSuspendedOnly]);

    const handleIsSuspendedOnlyChange = (value: string) => {
        setPage(1);
        setIsSuspendedOnly(value);
    };

    const [page, setPage] = useState(1);
    const size = 100;

    interface ISearchForm {
        keyword: string;
    }

    const { register, handleSubmit } = useForm<ISearchForm>({
        mode: "onChange",
    });

    const [searchKeyword, setSearchKeyword] = useState<string | null>(null);

    const onSubmit = ({ keyword }: ISearchForm) => {
        setPage(1);
        setSearchKeyword(keyword);
    };

    const { data: profileListData, isLoading: isProfileListDataLoading } = useQuery<IAdminProfileList>({
        queryKey: ['adminGetProfiles', page, size, searchKeyword, suspendedOnlyParam],
        queryFn: () => adminGetProfiles({ page, size, searchKeyword, isSuspendedOnly: suspendedOnlyParam }),
        enabled: true,
    });

    const totalPages = useMemo(() => profileListData?.totalPages ?? 0, [profileListData]);
    const currentPage = useMemo(() => profileListData?.currentPage ?? 0, [profileListData]);

    const pages = useMemo(() => {
        const startPage = Math.max(page - 4, 1);
        const endPage = Math.min(page + 5, totalPages);
        return Array.from({ length: Math.max(0, endPage - startPage + 1) }, (_, idx) => startPage + idx);
    }, [page, totalPages]);

    const profileColumns = [
        { key: "user.email", label: "🧑🏻‍🦲 이메일" },
        { key: "user.name", label: "🧑🏻‍🦲 이름" },
        { key: "user.type", label: "🧑🏻‍🦲 가입 유형" },
        { key: "nickname", label: "👤 닉네임" },
        { key: "avatar", label: "👤 프로필 사진" },
        { key: "bio", label: "👤 소개" },
        { key: "visibility", label: "👤 공개 여부" },
        { key: "suspends", label: "정지" },
        { key: "suspend", label: "정지하기" },
        { key: "createdAt", label: "👤 생성 일자" },
        { key: "updatedAt", label: "👤 수정 일자" },
    ];

    const { isOpen: isImageModalOpen, onOpen: onOpenImageModal, onClose: onCloseImageModal } = useDisclosure();
    const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);

    const openImage = (url: string) => {
        setSelectedImageUrl(url);
        onOpenImageModal();
    };

    const closeImage = () => {
        setSelectedImageUrl(null);
        onCloseImageModal();
    };

    /* create suspend modal */

    const { isOpen: isCreateSuspendModalOpen, onOpen: onOpenCreateSuspendModal, onClose: onCloseCreateSuspendModal } = useDisclosure();
    const [selectedSuspendProfile, setSelectedSuspendProfile] = useState<IAdminProfile | null>(null);
    const [suspendReason, setSuspendReason] = useState("");

    const openCreateSuspend = (profile: IAdminProfile) => {
        setSelectedSuspendProfile(profile);
        setSuspendReason("");
        onOpenCreateSuspendModal();
    };

    const closeCreateSuspend = () => {
        setSelectedSuspendProfile(null);
        setSuspendReason("");
        onCloseCreateSuspendModal();
    };

    /* view / cancel suspend modal */

    const { isOpen: isSuspendDetailModalOpen, onOpen: onOpenSuspendDetailModal, onClose: onCloseSuspendDetailModal } = useDisclosure();
    const [selectedSuspend, setSelectedSuspend] = useState<IAdminSuspend | null>(null);

    const openSuspendDetail = (suspend: IAdminSuspend) => {
        setSelectedSuspend(suspend);
        onOpenSuspendDetailModal();
    };

    const closeSuspendDetail = () => {
        setSelectedSuspend(null);
        onCloseSuspendDetailModal();
    };

    const renderCellValue = (profile: IAdminProfile, columnKey: string) => {
        if (columnKey === "user.email") {
            return String(profile.user?.email ?? "-");
        }
        if (columnKey === "user.name") {
            return String(profile.user?.name ?? "-");
        }
        if (columnKey === "user.type") {
            return String(profile.user?.type ?? "-");
        }
        if (columnKey === "avatar") {
            if (!profile.avatar) {
                return "-";
            }
            return (
                <Box
                    width={8}
                    height={8}
                    borderRadius={2}
                    border={"1px solid " + colors.g5}
                    backgroundColor={colors.g5}
                    onClick={() => openImage(profile.avatar)}
                    cursor={"pointer"}
                >
                    <Image src={profile.avatar} alt="avatar" width={"100%"} height={"100%"} objectFit={"cover"} />
                </Box>
            );
        }
        if (columnKey === "suspends") {
            const suspends = (profile.suspends ?? []).slice(0, 3);
            if (suspends.length === 0) {
                return "-";
            }
            return (
                <HStack gap={1}>
                    {suspends.map((suspend, index) => (
                        <Box
                            key={suspend.id}
                            as="button"
                            onClick={() => openSuspendDetail(suspend)}
                            fontSize={18}
                            lineHeight={1}
                            cursor={"pointer"}
                        >
                            {index === 2 
                                ? "🟥" : 
                                    suspend.isLifted 
                                        ? "🟩" : "🟨"}
                        </Box>
                    ))}
                </HStack>
            );
        }
        if (columnKey === "suspend") {
            const isSuspendDisabled = (profile.suspends?.length ?? 0) >= 3;
            return (
                <Button
                    size={"xs"}
                    variant={"solid"}
                    backgroundColor={colors.apple}
                    color={colors.white}
                    borderRadius={10}
                    _hover={{ bg: colors.apple }}
                    boxShadow="sm"
                    isDisabled={isSuspendDisabled}
                    opacity={isSuspendDisabled ? 0.3 : 1}
                    onClick={() => openCreateSuspend(profile)}
                >
                    정지하기
                </Button>
            );
        }
        if (columnKey === "createdAt" || columnKey === "updatedAt") {
            return formatDateTimeKor(profile[columnKey]);
        }
        return String(profile[columnKey as keyof IAdminProfile] ?? "-");
    };

    const [selectedProfileIds, setSelectedProfileIds] = useState<string[]>([]);
    const currentPageProfileIds = useMemo(() => profileListData?.profiles.map((profile) => profile.id) ?? [], [profileListData]);

    const isAllCurrentPageSelected = useMemo(() => {
        return currentPageProfileIds.length > 0 && currentPageProfileIds.every((id) => selectedProfileIds.includes(id));
    }, [currentPageProfileIds, selectedProfileIds]);

    const isSomeCurrentPageSelected = useMemo(() => {
        const someSelected = currentPageProfileIds.some((id) => selectedProfileIds.includes(id));
        return someSelected && !isAllCurrentPageSelected;
    }, [currentPageProfileIds, selectedProfileIds, isAllCurrentPageSelected]);

    const toggleSelectAllCurrentPage = () => {
        if (isAllCurrentPageSelected) {
            setSelectedProfileIds((prev) => prev.filter((id) => !currentPageProfileIds.includes(id)));
        } else {
            setSelectedProfileIds((prev) => Array.from(new Set([...prev, ...currentPageProfileIds])));
        }
    };

    const toggleSelectOne = (profileId: string) => {
        setSelectedProfileIds((prev) => (prev.includes(profileId) ? prev.filter((id) => id !== profileId) : [...prev, profileId]));
    };

    const { isOpen: isDeleteModalOpen, onOpen: onOpenDeleteModal, onClose: onCloseDeleteModal } = useDisclosure();

    const queryClient = useQueryClient();
    const toast = useToast();

    const deleteProfilesMutation = useMutation({
        mutationFn: adminDeleteProfiles,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetProfiles'] });
            setSelectedProfileIds([]);
        },
        onError: (error) => {
            console.error(error);
            toast({
                title: "프로필 삭제에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const onDeleteProfiles = () => {
        deleteProfilesMutation.mutate({ profileIds: selectedProfileIds });
        onCloseDeleteModal();
    };

    const suspendProfilesMutation = useMutation({
        mutationFn: adminSuspendProfiles,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetProfiles'] });
            closeCreateSuspend();
            toast({
                title: "프로필을 정지했습니다.",
                status: "success",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
        onError: (error) => {
            console.error(error);
            toast({
                title: "프로필 정지에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const onSuspendProfile = () => {
        if (!selectedSuspendProfile) return;
        suspendProfilesMutation.mutate({
            profileId: selectedSuspendProfile.id,
            reason: suspendReason,
        });
    };

    return (
        <AdminOnly>
            <Stack width={"100%"} padding={10}>
                <HStack justifyContent={"space-between"} alignItems={"center"} gap={10}>
                    <HStack justifyContent={"flex-start"} alignItems={"center"} gap={3}>
                        <Text fontSize={28} fontWeight={700} color={colors.black}>프로필</Text>
                        <Text fontSize={28} fontWeight={400} color={colors.gray}>{profileListData?.totalCount}</Text>
                    </HStack>
                    <HStack gap={3} />
                </HStack>

                <RadioGroup value={isSuspendedOnly} onChange={handleIsSuspendedOnlyChange}>
                    <HStack spacing={6} marginTop={4}>
                        <Box
                            paddingX={3}
                            paddingY={1}
                            backgroundColor={colors.light}
                            borderRadius={10}

                            width={"130px"}
                            textAlign={"center"}
                        >
                            <Text fontSize={12} fontWeight={500} color={colors.blue}>SUSPENDEDONLY</Text>
                        </Box>
                        <Radio value="all" colorScheme="blue">모두</Radio>
                        <Radio value="true" colorScheme="blue">TRUE</Radio>
                        <Radio value="false" colorScheme="blue">FALSE</Radio>
                    </HStack>
                </RadioGroup>
                
                <HStack as="form" onSubmit={handleSubmit(onSubmit)} marginY={10}>
                    <Input
                        {...register("keyword", { required: false })}
                        type="text"
                        placeholder="Search"
                        width={"50%"}
                    />
                    <Box as="button" type="submit">
                        <MyIcon src={"/svg/search.svg"} alt="search" width={24} height={24} />
                    </Box>
                </HStack>

                <HStack justifyContent={"flex-start"} marginY={0}>
                    <Button
                        size={"sm"}
                        variant={"solid"}
                        isDisabled={selectedProfileIds.length === 0}
                        opacity={selectedProfileIds.length === 0 ? 0.1 : 1}
                        onClick={onOpenDeleteModal}
                    >
                        <MyIcon src={"/svg/trash-filled.svg"} alt="trash" width={16} height={16} />
                    </Button>
                    <Modal isOpen={isDeleteModalOpen} onClose={onCloseDeleteModal}>
                        <ModalOverlay />
                        <ModalContent>
                            <ModalHeader>
                                <Text fontSize={20} fontWeight={700} color={colors.black}>삭제하기</Text>
                                <ModalCloseButton />
                            </ModalHeader>
                            <ModalBody>
                                <Text fontSize={16} fontWeight={400} color={colors.black}>선택된 행 수: {selectedProfileIds.length}</Text>
                                <Text fontSize={16} fontWeight={400} color={colors.black}>정말 삭제하시겠습니까?</Text>
                            </ModalBody>
                            <Stack marginTop={5} width={"100%"} padding={3}>
                                <Button
                                    onClick={onDeleteProfiles}
                                    isLoading={deleteProfilesMutation.isPending}
                                    width={"100%"}
                                    backgroundColor={colors.blue}
                                    color={colors.white}
                                    borderRadius={10}
                                    _hover={{ bg: colors.blue }}
                                    boxShadow="sm"
                                >
                                    삭제
                                </Button>
                            </Stack>
                        </ModalContent>
                    </Modal>
                </HStack>

                {!isProfileListDataLoading ? (
                    <TableContainer marginTop={5}>
                        <Table variant='simple' scrollBehavior="smooth">
                            <Thead backgroundColor={colors.g5}>
                                <Tr>
                                    <Th>
                                        <Checkbox
                                            isChecked={isAllCurrentPageSelected}
                                            isIndeterminate={isSomeCurrentPageSelected}
                                            onChange={toggleSelectAllCurrentPage}
                                        />
                                    </Th>
                                    {profileColumns.map((column) => (
                                        <Th key={column.key}>{column.label}</Th>
                                    ))}
                                </Tr>
                            </Thead>
                            <Tbody>
                                {profileListData?.profiles.map((profile) => (
                                    <Tr key={profile.id}>
                                        <Td>
                                            <Checkbox
                                                isChecked={selectedProfileIds.includes(profile.id)}
                                                onChange={() => toggleSelectOne(profile.id)}
                                            />
                                        </Td>
                                        {profileColumns.map((column) => (
                                            <Td key={column.key}>
                                                {renderCellValue(profile, column.key)}
                                            </Td>
                                        ))}
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Spinner color={colors.blue} />
                )}

                <HStack justifyContent={"center"} marginTop={5}>
                    {page > 1 && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page - 1)}>Previous</Button>
                    )}

                    {page > 5 && (
                        <>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(1)}>1</Button>
                            <Text>...</Text>
                        </>
                    )}

                    {pages.map((pageNumber) => (
                        <Button
                            key={pageNumber}
                            size={"xs"} variant={"ghost"}
                            onClick={() => setPage(pageNumber)}
                            backgroundColor={pageNumber === currentPage ? colors.blue : ""}
                            color={pageNumber === currentPage ? colors.white : ""}
                        >
                            {pageNumber}
                        </Button>
                    ))}

                    {page < totalPages - 4 && (
                        <>
                            <Text>...</Text>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(totalPages)}>{totalPages}</Button>
                        </>
                    )}

                    {page < totalPages && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page + 1)}>Next</Button>
                    )}
                </HStack>
            </Stack>

            {selectedImageUrl &&
                <ImageModal
                    isOpen={isImageModalOpen}
                    onClose={closeImage}
                    imageUrl={selectedImageUrl}
                />
            }

            {/* create suspend modal */}

            <Modal isOpen={isCreateSuspendModalOpen} onClose={closeCreateSuspend}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <Text fontSize={20} fontWeight={700} color={colors.black}>정지하기</Text>
                        <ModalCloseButton />
                    </ModalHeader>
                    <ModalBody>
                        <Stack gap={3}>
                            <Text fontSize={16} fontWeight={400} color={colors.black}>
                                닉네임: {selectedSuspendProfile?.nickname ?? "-"}
                            </Text>
                            <Text fontSize={14} fontWeight={400} color={colors.gray}>
                                정지 사유를 입력해주세요.
                            </Text>
                            <Textarea
                                value={suspendReason}
                                onChange={(e) => setSuspendReason(e.target.value)}
                                placeholder="정지 사유"
                                rows={4}
                            />
                        </Stack>
                    </ModalBody>
                    <Stack marginTop={5} width={"100%"} padding={3}>
                        <Button
                            onClick={onSuspendProfile}
                            isLoading={suspendProfilesMutation.isPending}
                            isDisabled={!suspendReason.trim()}
                            width={"100%"}
                            backgroundColor={colors.apple}
                            color={colors.white}
                            borderRadius={10}
                            _hover={{ bg: colors.apple }}
                            boxShadow="sm"
                        >
                            정지
                        </Button>
                    </Stack>
                </ModalContent>
            </Modal>

            {/* suspend detail / cancel modal */}

            {selectedSuspend && (
                <SuspendModal
                    isOpen={isSuspendDetailModalOpen}
                    onClose={closeSuspendDetail}
                    profileId={selectedSuspend.profileId}
                    suspendId={selectedSuspend.id}
                    reason={selectedSuspend.reason}
                    createdAt={selectedSuspend.createdAt}
                    isLifted={selectedSuspend.isLifted}
                />
            )}
        </AdminOnly>
    );
}
